import { create } from 'zustand'
import { api, setAccessToken, removeAccessToken } from '@/config/api'

interface User {
  email: string
  name: string
  picture?: string
  sub?: string
}

interface AuthState {
  user: User | null
  loading: boolean
  error: string | null
  checkAuth: () => Promise<void>
  loginWithGoogle: (idToken: string) => Promise<void>
  signOut: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  error: null,

  checkAuth: async () => {
    try {
      set({ loading: true, error: null })

      // 저장된 토큰이 없으면 바로 리턴
      const token = localStorage.getItem('access_token')
      if (!token) {
        set({ user: null, loading: false })
        return
      }

      // 백엔드 API를 통해 현재 사용자 정보 확인
      const { data } = await api.get('/auth/me')
      set({ user: { email: data.email, name: data.name || data.email, picture: data.picture, sub: data.sub } })
    } catch {
      removeAccessToken()
      set({ user: null })
    } finally {
      set({ loading: false })
    }
  },

  loginWithGoogle: async (idToken: string) => {
    try {
      set({ loading: true, error: null })

      // Google ID Token을 백엔드로 전송하여 JWT 발급
      const { data } = await api.post('/auth/google', { id_token: idToken })

      // JWT 토큰 저장
      setAccessToken(data.access_token)

      // 사용자 정보 설정
      set({
        user: {
          email: data.user.email,
          name: data.user.name,
          picture: data.user.picture,
          sub: data.user.sub,
        },
      })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '로그인에 실패했습니다.'
      set({ error: errorMessage })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  signOut: () => {
    removeAccessToken()
    set({ user: null, error: null })
    window.location.href = '/login'
  },
}))

// 앱 시작 시 인증 상태 확인
useAuthStore.getState().checkAuth()
